import { gql, useMutation } from '@apollo/client'
import pick from 'lodash/pick'
import { useEffect, useState } from 'react'

import {
  ArtistCreateEventInput,
  ArtistCreateEventMutation,
  ArtistCreateEventMutationVariables,
  EventStatus,
} from '@/clients/utoniq-core/schema'
import { getLocation } from '@/components/pages/ConsoleEventEdit/viewModel'
import { config } from '@/config'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { Camera } from '@/core/native'
import { useLocationWatcher } from '@/core/native/geolocation'
import {
  getDetail,
  getPlacePredictions,
  Place,
  Prediction,
} from '@/lib/googleMap'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleEventCreate')
  const { user } = useCurrentUser()
  const { router } = useRouter()
  const { showMessage, showError } = useMessage()
  const { state, actions } = useViewModelEvent(user?.uid ?? '')

  const [artistCreateEvent] = useMutation<
    ArtistCreateEventMutation,
    ArtistCreateEventMutationVariables
  >(ARTIST_CREATE_EVENT)

  return {
    state: {
      ...state,
      artistId: user?.uid ?? '',
    },
    actions: {
      ...actions,
      async onSubmit(values) {
        const inputs: ArtistCreateEventInput = {
          ...pick(values, ['title', 'description', 'thumbnailPath']),
          note: values?.note || null,
          location: getLocation(values?.location),
          startedAt: values?.startedAt || null,
          endedAt: values?.endedAt || null,
          status: values?.isPublished
            ? EventStatus.Published
            : EventStatus.Unpublished,
          closeDate: values.closeDate
            .filter(({ date }) => !!date)
            .map(({ date }) => date),
          publishStartedAt: values?.publishStartedAt || new Date(),
          publishEndedAt: values?.publishEndedAt || null,
        }
        await artistCreateEvent({ variables: { inputs } })
          .then(() => {
            router.goBack()
            showMessage(t('createEventSuccess'), 'success')
          })
          .catch(showError)
      },
    },
  }
})

const ARTIST_CREATE_EVENT = gql`
  mutation ArtistCreateEvent($inputs: ArtistCreateEventInput!) {
    artistCreateEvent(inputs: $inputs) {
      id
    }
  }
`

export const useViewModelEvent = (
  userId: string
): {
  state: Pick<State, 'progress' | 'place' | 'gpsClientInfo' | 'predictions'>
  actions: Pick<
    Action,
    'onDrop' | 'onRequestGPSPermission' | 'onPlaceSearch' | 'onSelectPlace'
  >
} => {
  const { t } = useTranslation('consoleEventCreate')
  const { showError } = useMessage()
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [place, setPlace] = useState<Place | null>(null)

  const { getImage } = Camera
  const { upload, progress } = useUserTmpUploader(userId)
  const { permission, currentLocation, requestPermission, watch, unwatch } =
    useLocationWatcher()

  useEffect(() => {
    if (!permission) return
    watch().catch(showError)
    return () => {
      unwatch().catch(showError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, unwatch, watch])

  const getResource = async () => {
    const file = await getImage(config.upload.mimeTypes.image)
    if (file.size > config.upload.limit['image']) {
      throw new Error('uploadErrorMaximumSize')
    }
    return file
  }

  return {
    state: {
      progress,
      predictions,
      place,
      gpsClientInfo: {
        permission,
        currentLocation,
      },
    },
    actions: {
      async onDrop() {
        const file = await getResource().catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(file)
        const uuid = createUuid()
        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onPlaceSearch({ keyword }) {
        if (!keyword) {
          return
        }
        const predictions = await getPlacePredictions(keyword)
        setPredictions(predictions)
      },
      async onSelectPlace({ placeId, map }) {
        const result = await getDetail({ placeId, map })
        if (result.isErr()) {
          throw result.err()
        }
        setPlace(result.unwrap())
      },
      async onRequestGPSPermission() {
        if (permission) return
        await requestPermission()
      },
    },
  }
}
